import { useEffect, useState } from "react";
import { MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { getMobileContext } from "@/utils/deviceDetection";
import { cn } from "@/lib/utils";

export interface GetTheAppNavButtonProps {
  className?: string;
}

const WEB_BOOKING_PATH = "/search?focus=location";

/**
 * Mobile web only (< md): primary CTA aligned with desktop “Connexion” / brand actions.
 * Points to booking on the website while the native app is not promoted.
 */
export const GetTheAppNavButton = ({ className }: GetTheAppNavButtonProps) => {
  const { t } = useTranslation();
  const [isHidden, setIsHidden] = useState(false);

  useEffect(() => {
    try {
      if (getMobileContext().isStandalone) {
        setIsHidden(true);
      }
    } catch {
      setIsHidden(false);
    }
  }, []);

  if (isHidden) {
    return null;
  }

  return (
    <Button
      asChild
      variant="default"
      size="sm"
      className={cn(
        "h-9 min-h-[2.25rem] shrink-0 touch-manipulation rounded-xl border-0 px-3 font-semibold",
        "bg-green-night text-white shadow-sm shadow-green-night/25",
        "gap-1.5 text-xs transition-[background-color,box-shadow,transform] duration-300 ease-out hover:bg-green-light hover:shadow-md hover:shadow-green-night/30 hover:scale-[1.01] active:scale-[0.98]",
        "sm:gap-2 sm:px-3.5 sm:text-sm",
        "max-w-[9.75rem] sm:max-w-[13rem]",
        "focus-visible:ring-2 focus-visible:ring-emerald-400/90 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        "[&_svg]:size-3.5 sm:[&_svg]:size-4",
        className
      )}
    >
      <Link to={WEB_BOOKING_PATH} aria-label={t("nav.getTheAppAria")}>
        <MapPin className="shrink-0 opacity-95" aria-hidden />
        <span className="min-w-0 truncate leading-tight text-white">{t("nav.getTheApp")}</span>
      </Link>
    </Button>
  );
};
